import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    DeleteOutlined,
    EditOutlined,
    MenuUnfoldOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { Drawer, Flex } from 'antd';
import cn from 'classnames';

import { useAutoLoadPatterns } from '@/features/load-patterns';
import { type PatternStudy, setStudiedPatterns } from '@/features/pattern-lab';

import {
    type Pattern,
    type PatternFormValues,
    PatternModal,
    useDeletePatternMutation,
    useToggleFlagFieldMutation,
    useUpdatePatternMutation,
} from '@/entities/pattern';
import {
    EmptyPatternWatchlist,
    PatternWatchlistSelector,
    PatternWatchlistTitle,
    useGetPatternWatchlistsQuery,
    useSelectedPatternWatchlistId,
} from '@/entities/pattern-watchlist';

import { getPatternStudioLabPath } from '@/shared/constants/router';
import { useAntMessage } from '@/shared/lib/hooks';
import { logError } from '@/shared/lib/logging';
import { roundToDecimals } from '@/shared/lib/math';
import { useAppDispatch } from '@/shared/lib/store';
import { Button } from '@/shared/ui/button';
import { Popover } from '@/shared/ui/popover';
import {
    ConfigurableTable,
    createColorColumnFormatter,
    createDirectionTypeFormatter,
    createFlagColumnFormatter,
    createPercentageBadgeFormatter,
    useTableColumnsConfig,
} from '@/shared/ui/table';

import cls from './pattern-watchlist-as-drawer.module.css';

export function PatternWatchlistAsDrawer() {
    const { t } = useTranslation(['pattern-studio', 'table', 'pattern']);
    const { success } = useAntMessage();
    const [deletePattern, deletePatternResult] = useDeletePatternMutation();
    const [updatePattern, updatePatternResult] = useUpdatePatternMutation();
    const [toggleFlagField] = useToggleFlagFieldMutation();
    const dispatch = useAppDispatch();
    const { data: watchlists = [] } = useGetPatternWatchlistsQuery();
    const {
        patterns,
        isLoading: isPatternsLoading,
        isFetching: isPatternsFetching,
    } = useAutoLoadPatterns();
    const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
    const [editingPattern, setEditingPattern] = useState<Pattern | null>(null);
    const [pendingEditIds, setPendingEditIds] = useState<number[]>([]);
    const [isOpened, setIsOpened] = useState<boolean>(false);
    const selectedPatternWatchlistId = useSelectedPatternWatchlistId();
    const navigate = useNavigate();

    const {
        isOpen: isColumnsConfigOpen,
        onClose: onColumnsConfigClose,
        toggle: toggleColumnsConfig,
    } = useTableColumnsConfig();

    const isTableLoading =
        isPatternsLoading ||
        isPatternsFetching ||
        deletePatternResult.isLoading ||
        updatePatternResult.isLoading ||
        pendingDeleteIds.length > 0 ||
        pendingEditIds.length > 0;

    const handleEditClose = useCallback(() => {
        setEditingPattern(null);
    }, []);

    const handleEditSubmit = useCallback(
        async (values: PatternFormValues) => {
            if (!editingPattern || !selectedPatternWatchlistId) return;

            try {
                setPendingEditIds((prev) => [...prev, editingPattern.id]);

                await updatePattern({
                    id: editingPattern.id,
                    pattern_name: values.pattern_name,
                    color: values.color,
                });

                setEditingPattern(null);

                setPendingEditIds((prev) =>
                    prev.filter((pendingId) => pendingId !== editingPattern.id),
                );
            } catch (error) {
                logError('Failed to update pattern:', error);
                setPendingEditIds((prev) =>
                    prev.filter((pendingId) => pendingId !== editingPattern.id),
                );
            }
        },
        [editingPattern, updatePattern, selectedPatternWatchlistId],
    );

    const onOpen = useCallback(() => {
        setIsOpened(true);
    }, []);

    const onClose = useCallback(() => {
        setIsOpened(false);
    }, []);

    const isPatternDeleting = useCallback(
        (patternId: number) =>
            (deletePatternResult.isLoading &&
                deletePatternResult.originalArgs === patternId) ||
            pendingDeleteIds.includes(patternId),
        [deletePatternResult, pendingDeleteIds],
    );

    const handleFlagChange = useCallback(
        async (id: number, checked: boolean) => {
            if (!selectedPatternWatchlistId) return;

            try {
                await toggleFlagField({
                    id,
                    flag_field: checked,
                });
            } catch (error) {
                logError('Failed to toggle pattern flag:', error);
            }
        },
        [selectedPatternWatchlistId, toggleFlagField],
    );

    const handleDelete = useCallback(
        async (id: number) => {
            try {
                setPendingDeleteIds((prev) => [...prev, id]);

                await deletePattern(id);

                success?.({
                    content: t('pattern:deleteSuccess'),
                });

                setPendingDeleteIds((prev) =>
                    prev.filter((pendingId) => pendingId !== id),
                );
            } catch (error) {
                logError('Failed to delete pattern:', error);
                setPendingDeleteIds((prev) =>
                    prev.filter((pendingId) => pendingId !== id),
                );
            }
        },
        [deletePattern, success, t],
    );

    const isPatternEditing = useCallback(
        (patternId: number) =>
            (updatePatternResult.isLoading &&
                updatePatternResult.originalArgs?.id === patternId) ||
            pendingEditIds.includes(patternId),
        [updatePatternResult, pendingEditIds],
    );

    const handleGoClick = useCallback(
        (pattern: Pattern) => {
            const study: PatternStudy = {
                pattern,
                color: pattern.color || '#0024ff',
                name: pattern.pattern_name,
            };

            dispatch(setStudiedPatterns([study]));
            navigate(getPatternStudioLabPath());
            onClose();
        },
        [dispatch, navigate, onClose],
    );

    const handleEdit = useCallback((pattern: Pattern) => {
        setEditingPattern(pattern);
    }, []);

    const editModalData = useMemo(
        () =>
            editingPattern
                ? {
                      pattern_name: editingPattern.pattern_name!,
                      color: editingPattern.color!,
                  }
                : { pattern_name: '', color: '' },
        [editingPattern],
    );

    const extendedColumns = useMemo(
        () => ({
            flag_field: {
                title: '',
                render: createFlagColumnFormatter<Pattern>(
                    (record) => record.flag_field ?? false,
                    handleFlagChange,
                    (record) => record.id,
                    (record) => record.color,
                ),
                disableVisibilityToggle: true,
                order: -2,
            },
            color: {
                title: t('table:columns.color'),
                key: 'color',
                render: createColorColumnFormatter<Pattern>(
                    (record) => record.color,
                ),
            },
            price_drawdown_post_mean: {
                key: 'price_drawdown_post_mean',
                render: createPercentageBadgeFormatter<Pattern>(
                    (record: Pattern) => record.price_drawdown_post_mean,
                    {
                        precision: 2,
                    },
                ),
            },
            direction_type: {
                key: 'direction_type',
                render: createDirectionTypeFormatter<Pattern>(
                    (record: Pattern) => record.direction_type,
                ),
            },
            price_reg_post_mean: {
                key: 'price_reg_post_mean',
                render: (_: unknown, record: Pattern) =>
                    `${roundToDecimals(Math.abs(record.price_reg_post_mean))}%`,
            },
            price_threshold_post_mean: {
                key: 'price_threshold_post_mean',
                render: createPercentageBadgeFormatter<Pattern>(
                    (record: Pattern) =>
                        roundToDecimals(record.price_threshold_post_mean),
                ),
            },
            go: {
                title: t('table:columns.go'),
                key: 'go',
                render: (_: unknown, record: Pattern) => (
                    <Button
                        onClick={() => handleGoClick(record)}
                        title={t('actions.go')}
                    />
                ),
                disableVisibilityToggle: true,
                order: 50,
            },
            delete: {
                title: '',
                render: (_: unknown, record: Pattern) => {
                    const isDeleting = isPatternDeleting(record.id);

                    return (
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            isLoading={isDeleting}
                            onClick={() => handleDelete(record.id)}
                            variant="ghost"
                        />
                    );
                },
                disableVisibilityToggle: true,
                order: 52,
            },
            edit: {
                title: '',
                render: (_: unknown, record: Pattern) => {
                    const isEditing = isPatternEditing(record.id);

                    return (
                        <Button
                            icon={<EditOutlined />}
                            isLoading={isEditing}
                            onClick={() => handleEdit(record)}
                            variant="ghost"
                        />
                    );
                },
                disableVisibilityToggle: true,
                order: 53,
            },
        }),
        [
            t,
            handleDelete,
            handleFlagChange,
            isPatternDeleting,
            handleGoClick,
            handleEdit,
            isPatternEditing,
        ],
    );

    const renderColumnConfigButton = () => {
        const columnConfigContent = (
            <div className={cls.columnConfig}>
                <ConfigurableTable
                    extendedColumns={extendedColumns}
                    isConfigOnly
                    tableId="MY_AI_PATTERN"
                />
            </div>
        );

        return (
            <Popover
                content={columnConfigContent}
                onOpenChange={(open) => {
                    if (!open) {
                        onColumnsConfigClose();
                    }
                }}
                open={isColumnsConfigOpen}
                trigger={
                    <Button
                        icon={<MenuUnfoldOutlined />}
                        onClick={toggleColumnsConfig}
                    />
                }
            />
        );
    };

    return (
        <>
            <Flex
                align="center"
                className={cls.root}
                justify="space-between"
            >
                <PatternWatchlistTitle />
                <Button
                    icon={<UnorderedListOutlined />}
                    onClick={onOpen}
                    size="small"
                    variant="ghost"
                />
            </Flex>
            <Drawer
                closable={false}
                onClose={onClose}
                open={isOpened}
                placement="bottom"
                rootClassName={cn(cls.drawer)}
                title={<PatternWatchlistTitle />}
                width="85vh"
            >
                {watchlists.length > 0 ? (
                    <>
                        <div className={cn(cls.padding, cls.actionContainer)}>
                            <div
                                style={{
                                    flexGrow: 1,
                                    display: 'inline-block',
                                }}
                            >
                                <PatternWatchlistSelector />
                            </div>
                            {renderColumnConfigButton()}
                        </div>
                        <ConfigurableTable
                            dataSource={patterns ?? []}
                            emptyDescription={t(
                                'emptyState.watchlist.zeroPattern',
                            )}
                            extendedColumns={extendedColumns}
                            isConfigOpen={isColumnsConfigOpen}
                            loading={isTableLoading}
                            onConfigClose={onColumnsConfigClose}
                            pagination={false}
                            rowKey="unique_id"
                            size="small"
                            tableId="MY_AI_PATTERN"
                        />
                    </>
                ) : (
                    <EmptyPatternWatchlist />
                )}
            </Drawer>
            {editingPattern && (
                <PatternModal
                    data={editModalData}
                    isLoading={updatePatternResult.isLoading}
                    isOpen={!!editingPattern}
                    mode="edit"
                    onClose={handleEditClose}
                    onSubmit={handleEditSubmit}
                />
            )}
        </>
    );
}

PatternWatchlistAsDrawer.displayName = 'PatternWatchlistAsDrawer';
